const OBJECT_ID_RE = /^[a-fA-F0-9]{24}$/;

export const normalizeProviderText = (value) =>
  `${value || ""}`.toLowerCase().replace(/\s+/g, " ").trim();

export const isPcpSpecialty = (provider) => {
  const specialty = normalizeProviderText(provider?.specialty || provider?.data?.specialty);
  return specialty === "family medicine (pcp)" || specialty === "family medicine pcp";
};

export const isActiveProvider = (provider) =>
  normalizeProviderText(provider?.status || "active") === "active";

export const resourceRecordId = (record) =>
  `${record?.id ?? record?._id ?? ""}`.trim();

export const looksLikeObjectId = (value) => OBJECT_ID_RE.test(`${value || ""}`.trim());

export const providerNameKeys = (provider) =>
  [provider?.name, provider?.name_original, provider?.office_name, provider?.name_branch]
    .map(normalizeProviderText)
    .filter(Boolean);

const compactProviderName = (value) =>
  normalizeProviderText(value).replace(/[^a-z0-9\u4e00-\u9fff]+/g, "");

const namesLooselyMatch = (storedName, provider) => {
  const needle = normalizeProviderText(storedName);
  const compactNeedle = compactProviderName(storedName);
  if (!needle) return false;
  const keys = providerNameKeys(provider);
  if (keys.includes(needle)) return true;
  if (compactNeedle && keys.map(compactProviderName).includes(compactNeedle)) return true;
  return keys.some((key) => {
    if (!key) return false;
    return needle.startsWith(`${key} `) || needle.startsWith(`${key},`) || needle.startsWith(`${key}|`);
  });
};

export const findMatchingProvider = (providers = [], { storedName, storedId } = {}) => {
  const list = Array.isArray(providers) ? providers : [];
  const id = `${storedId || ""}`.trim();
  if (id && looksLikeObjectId(id)) {
    const byId = list.find((provider) => resourceRecordId(provider) === id);
    if (byId) return byId;
  }
  const needle = normalizeProviderText(storedName);
  if (!needle) return null;
  const exact = list.find((provider) => namesLooselyMatch(storedName, provider));
  if (exact) return exact;
  const prefixMatches = list.filter((provider) =>
    providerNameKeys(provider).some(
      (key) => key.startsWith(`${needle} `) || key.startsWith(`${needle},`)
    )
  );
  return prefixMatches.length === 1 ? prefixMatches[0] : null;
};

export const resolvePcpSelectValue = (customer, providers = []) => {
  const storedName = customer?.primary_care_physician || customer?.care_provider || "";
  const match = findMatchingProvider(providers, {
    storedName,
    storedId: customer?.primary_care_physician_id,
  });
  if (match) {
    return { value: resourceRecordId(match), label: match.name || storedName };
  }
  if (!`${storedName}`.trim()) return "";
  return { label: storedName, value: customer?.primary_care_physician_id || "" };
};

export const resolvePharmacySelectValue = (customer, pharmacies = []) => {
  const storedName = customer?.pharmacy || "";
  const match = findMatchingProvider(pharmacies, {
    storedName,
    storedId: customer?.pharmacy_id,
  });
  if (match) {
    return { value: resourceRecordId(match), label: match.name || storedName };
  }
  if (!`${storedName}`.trim()) return "";
  return { label: storedName, value: customer?.pharmacy_id || "" };
};
